// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import { ResolvedHighlight } from './highlights';

import styles from './VariableHighlightOverlay.module.css';

export interface VariableHighlightOverlayProps {
  /**
   * Pre-resolved highlights (one per matched ident).  The resolver in
   * `resolveHighlights.ts` is responsible for the join against
   * `ViewElement`; the renderer never reaches into model state.
   */
  highlights: ReadonlyArray<ResolvedHighlight>;
}

/**
 * D3 acceptance criterion 29 -- variable-highlight overlay layer.
 *
 * Renders metadata-driven visual chrome around a set of named
 * variables.  Three render kinds are supported:
 *
 *   - `border`: stroked ring, opaque.  Reads as a callout.
 *   - `glow`:   wide translucent ring.  Reads as a halo.
 *   - `badge`:  small filled disc with a short text label, anchored
 *               at the top-right corner of the variable.
 *
 * Like the C28 annotation overlay, this component:
 *
 *   1. Contains no engine imports.  The resolver does the join
 *      between caller metadata and view elements; the renderer sees
 *      only the resolved coordinates.  This is enforced by the
 *      source audit in
 *      [tests/variable-highlight-readonly.test.ts](../tests/variable-highlight-readonly.test.ts).
 *
 *   2. Takes no callbacks.  Highlights are pure decoration --
 *      clicking on one passes through to the diagram element
 *      underneath via `pointer-events: none`.  A future "highlight
 *      tooltip on hover" feature would need a separate component
 *      with an explicit pointer-events policy.
 */
export const VariableHighlightOverlay = React.memo(function VariableHighlightOverlay(
  props: VariableHighlightOverlayProps,
): React.ReactElement | null {
  const { highlights } = props;

  if (highlights.length === 0) {
    return null;
  }

  return (
    <g className={clsx(styles.overlay, 'simlin-variable-highlight-overlay')}>
      {highlights.map(renderHighlight)}
    </g>
  );
});

function renderHighlight(h: ResolvedHighlight): React.ReactElement | null {
  switch (h.kind) {
    case 'border':
      return (
        <circle
          key={`border-${h.uid}`}
          className={clsx(styles.borderRing, 'simlin-variable-highlight-border')}
          cx={h.cx}
          cy={h.cy}
          r={h.radius}
          stroke={h.color}
          // Inline style so a caller-supplied color wins over the class default:
          // a CSS `stroke` on .borderRing/.glowRing otherwise overrides the
          // presentation attribute, which silently discarded the loop's
          // polarity color (NC-2). Undefined color falls back to the class.
          style={h.color ? { stroke: h.color } : undefined}
        />
      );
    case 'glow':
      return (
        <circle
          key={`glow-${h.uid}`}
          className={clsx(styles.glowRing, 'simlin-variable-highlight-glow')}
          cx={h.cx}
          cy={h.cy}
          r={h.radius}
          stroke={h.color}
          style={h.color ? { stroke: h.color } : undefined}
        />
      );
    case 'badge':
      return (
        <g
          key={`badge-${h.uid}`}
          className="simlin-variable-highlight-badge"
        >
          <circle
            className={clsx(styles.badgeDisc, 'simlin-variable-highlight-badge-disc')}
            cx={h.cx}
            cy={h.cy}
            r={h.radius}
            fill={h.color}
          />
          {h.badgeLabel !== undefined ? (
            <text
              className={clsx(styles.badgeText, 'simlin-variable-highlight-badge-text')}
              x={h.cx}
              y={h.cy}
            >
              {h.badgeLabel}
            </text>
          ) : null}
        </g>
      );
    default:
      return null;
  }
}
